<script setup lang="ts">
import { ref } from 'vue';
import { UiFieldContainer, UiIcon } from '@shopexperts/shared';

// LinkedIn input per Figma: 24px LinkedIn icon + 1px #D6D6D6 divider inside the
// field (pl-8 pr-16 py-8), composed with UiFieldContainer for hint/error wiring.
interface LinkedInInputFieldProps {
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  error?: string;
}

const props = withDefaults(defineProps<LinkedInInputFieldProps>(), {
  placeholder: 'Enter your LinkedIn profile',
  disabled: false,
});

const model = defineModel<string>();

const emit = defineEmits<{
  (e: 'validate'): void;
}>();

const inputRef = ref<HTMLInputElement>();
const isFocused = ref(false);

function handleInput(event: Event) {
  model.value = (event.target as HTMLInputElement).value;
}

function focusInput() {
  isFocused.value = true;
  inputRef.value?.focus();
}

function blurInput() {
  isFocused.value = false;
  emit('validate');
}
</script>

<template>
  <UiFieldContainer
    class="pl-2! pr-4!"
    gap="2"
    :is-focused="isFocused"
    :invalid="props.invalid"
    :error="props.error"
    :described-by-id-base="props.id || props.name"
    :disabled="props.disabled"
    @focus="focusInput"
    @blur="blurInput"
  >
    <UiIcon name="linkedin" class="size-6 shrink-0" />
    <span class="h-6 w-px shrink-0 bg-se-gray-300" aria-hidden="true" />
    <input
      :id="props.id || props.name"
      ref="inputRef"
      :value="model"
      :name="props.name"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      type="text"
      inputmode="url"
      class="flex-1 font-archivo"
      :aria-invalid="props.invalid ? 'true' : undefined"
      :aria-describedby="props.error ? `${props.id || props.name}-error` : undefined"
      @input="handleInput"
      @focus="isFocused = true"
      @blur="blurInput"
    />
  </UiFieldContainer>
</template>
